import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  createCatalogProductRequest,
  getCatalogRequest,
  updateCatalogProductRequest,
  uploadCatalogRequest,
} from './catalogApi';
import { ALL_CLIENTS, MAX_OPTION_PAGES } from './catalogConstants';
import { selectCurrentWarehouseId } from '../warehouseContext/warehouseContextSlice';
import { extractApiError } from '../../utils/errors';

/**
 * Loads the page of products the filters describe.
 *
 * The warehouse in use does not narrow the list: a catalog belongs to the
 * company, and it is the same one in every centre that company stores in. It
 * does decide the stock of every row — the balance in that warehouse, or in
 * all of them when none is picked.
 *
 * @returns {Promise<{count: number, results: Array<object>}>}
 */
export const fetchCatalog = createAsyncThunk(
  'catalog/fetchCatalog',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { search, company, onlyActive, onlyWithStock, page, rowsPerPage } = state.catalog.filters;

      const params = {
        page,
        page_size: rowsPerPage,
      };
      const warehouseId = selectCurrentWarehouseId(state);
      if (warehouseId) params.warehouse = warehouseId;
      if (company !== ALL_CLIENTS) params.company = company;
      if (search) params.search = search;
      if (onlyActive) params.is_active = '1';
      if (onlyWithStock) params.with_stock = '1';

      const response = await getCatalogRequest(params);
      return response.data;
    } catch (error) {
      console.error('Error fetching catalog:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo obtener el catálogo'));
    }
  }
);

/**
 * The active products of one company, for the pickers that need to choose
 * among all of them: the components of a kit, the lines of a receipt.
 *
 * The listing is paginated and capped at a hundred a page, so the pages are
 * walked here. It stops at `MAX_OPTION_PAGES`: a picker over more products
 * than that needs a search on the server, not a longer list.
 *
 * @param {number} companyId
 * @returns {Promise<{companyId: number, products: Array<object>}>}
 */
export const fetchProductOptions = createAsyncThunk(
  'catalog/fetchProductOptions',
  async (companyId, { rejectWithValue }) => {
    try {
      const products = [];

      for (let page = 1; page <= MAX_OPTION_PAGES; page += 1) {
        const response = await getCatalogRequest({
          company: companyId,
          is_active: '1',
          page,
          page_size: 100,
        });
        products.push(...(response.data?.results || []));
        if (!response.data?.next) break;
      }

      return { companyId, products };
    } catch (error) {
      console.error('Error fetching product options:', error);
      return rejectWithValue(extractApiError(error, 'No se pudieron obtener los productos'));
    }
  }
);

/*
 * The writes reload the listing instead of patching it: the order and the
 * filters are the server's, and so is the stock of every row.
 *
 * Their errors are read with `extractApiError` because the backend writes the
 * sentence for the person — "Ñandú SpA ya tiene un producto con el SKU POL-001"
 * — and a field error arrives as `{campo: [mensaje]}`.
 */

/**
 * @param {{companyId: number, values: object}} params - See `buildProductPayload`.
 */
export const createProduct = createAsyncThunk(
  'catalog/createProduct',
  async ({ companyId, values }, { dispatch, rejectWithValue }) => {
    try {
      const response = await createCatalogProductRequest({ company_id: companyId, ...values });
      dispatch(fetchCatalog());
      return response.data;
    } catch (error) {
      console.error('Error creating product:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo crear el producto'));
    }
  }
);

/**
 * @param {{productId: number, values: object}} params - Only the fields to change.
 */
export const updateProduct = createAsyncThunk(
  'catalog/updateProduct',
  async ({ productId, values }, { dispatch, rejectWithValue }) => {
    try {
      const response = await updateCatalogProductRequest(productId, values);
      dispatch(fetchCatalog());
      return response.data;
    } catch (error) {
      console.error('Error updating product:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo guardar el producto'));
    }
  }
);

/**
 * Loads a sheet of products into one company's catalog.
 *
 * Resolves with the server's own summary — "3 productos creados y 2
 * actualizados en Ñandú SpA" — which is what the screen shows.
 *
 * @param {{companyId: number, file: File}} params
 * @returns {Promise<{message: string, created: number, updated: number}>}
 */
export const uploadCatalog = createAsyncThunk(
  'catalog/uploadCatalog',
  async ({ companyId, file }, { dispatch, rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('excel_file', file);
      formData.append('company_id', companyId);

      const response = await uploadCatalogRequest(formData);
      dispatch(fetchCatalog());
      return response.data;
    } catch (error) {
      console.error('Error uploading catalog:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo cargar la planilla'));
    }
  }
);
